import React from 'react';
import { ReviewedValidationCell, ReviewedValidationCheckRepresentation } from '~/data/openapi-client/index';
import { CellRange } from '~/app/components/visual-excel/worksheet';

export type SelectRangeHandler = (fileId: string, sheet: string, range: CellRange) => void;

export interface VisualExcelMapping {
  fileId: string;
  sheet: string;
  range: CellRange;
}

export interface CheckSegment {
  value: string;
  visualExcelMapping?: VisualExcelMapping;
}

type CellValues = ReviewedValidationCheckRepresentation['cellValues'];

/** Which part of a cell value is looked up: the variable in the expression, or the substituted amount. */
export type CheckSegmentKind = 'expression' | 'value';

const getCellText = (cell: ReviewedValidationCell, kind: CheckSegmentKind) =>
  kind === 'expression' ? cell.expression : cell.value?.toString();

/**
 * A cell without row/column coordinates cannot be located in the visual Excel, so it gets no mapping
 * and is rendered as plain text instead of a link that would silently do nothing when clicked.
 */
const getVisualExcelMapping = (
  cell: ReviewedValidationCell,
  fallbackFileId: string,
): VisualExcelMapping | undefined => {
  if (cell.row == null || cell.column == null) return undefined;

  const start = { row: Number(cell.row) - 1, col: Number(cell.column) - 1 };
  // A check that names only a row (or only a column) covers that whole band of the table rather than one
  // cell, and the backend reports the far end of it. Falling back to the start keeps single cells a point.
  const end = {
    row: cell.rowEnd == null ? start.row : Number(cell.rowEnd) - 1,
    col: cell.columnEnd == null ? start.col : Number(cell.columnEnd) - 1,
  };

  return {
    fileId: cell.combinationFileId ?? fallbackFileId,
    sheet: cell.sheet ?? '',
    range: { start, end },
  };
};

/**
 * The mapping of the first cell value that can actually be located in the visual Excel, used to jump to a
 * check's cells when it is expanded. Returns undefined when no cell value has coordinates, so callers can
 * leave the workbook where it is instead of scrolling it to a cell that does not exist.
 */
export const getFirstLocatableMapping = (
  cellValues: CellValues,
  fallbackFileId: string,
): VisualExcelMapping | undefined => {
  const cell = cellValues?.find((candidate) => candidate.row != null && candidate.column != null);

  return cell && getVisualExcelMapping(cell, fallbackFileId);
};

/**
 * Splits an expression (or its substituted result) into plain text and linkable cell value segments.
 * Returns an empty array when nothing could be linked, so callers can fall back to the raw string.
 */
export const getCheckSegments = (
  text: string,
  cellValues: CellValues,
  fallbackFileId: string,
  kind: CheckSegmentKind,
): CheckSegment[] => {
  const segments: CheckSegment[] = [];
  // The same variable can occur several times in one expression and the backend emits one cell value per
  // occurrence, so remember where each string last matched to map the Nth cell value to the Nth occurrence.
  const searchOffsets = new Map<string, number>();
  let prevIndex = 0;

  cellValues?.forEach((cell) => {
    const cellText = getCellText(cell, kind);
    if (!cellText) return;

    const startIndex = text.indexOf(cellText, searchOffsets.get(cellText) ?? 0);
    if (startIndex === -1) return;

    const endIndex = startIndex + cellText.length;
    searchOffsets.set(cellText, endIndex);

    segments.push({ value: text.slice(prevIndex, startIndex) });
    segments.push({ value: cellText, visualExcelMapping: getVisualExcelMapping(cell, fallbackFileId) });
    prevIndex = endIndex;
  });

  if (!segments.length) return segments;

  segments.push({ value: text.slice(prevIndex) });

  return segments;
};

const renderSegments = (segments: CheckSegment[], onSelectRange: SelectRangeHandler) =>
  segments.map((segment, i) => (
    <React.Fragment key={i}>
      {segment.visualExcelMapping ? (
        <span
          role="button"
          className="underline"
          data-expression={i}
          onClick={() => {
            if (segment.visualExcelMapping) {
              onSelectRange(
                segment.visualExcelMapping.fileId,
                segment.visualExcelMapping.sheet,
                segment.visualExcelMapping.range,
              );
            }
          }}
        >
          {segment.value}
        </span>
      ) : (
        segment.value
      )}
    </React.Fragment>
  ));

/** Renders a check expression (or result) with a link on every cell value that can be located in the visual Excel. */
export const renderCheckText = (
  text: string,
  cellValues: CellValues,
  fallbackFileId: string,
  kind: CheckSegmentKind,
  onSelectRange: SelectRangeHandler,
) => {
  const segments = getCheckSegments(text, cellValues, fallbackFileId, kind);

  if (!segments.length) return text;

  return renderSegments(segments, onSelectRange);
};
